import { useMemo, useRef, useState } from "react";
import { bool, number, string, func, node, oneOfType, arrayOf } from "prop-types";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { templateEditor } from "@paths";
import { getTemplatePreviewQuery, saveTemplateQuery } from "@query";
import { useActiveProjectStore, useStores, useTemplates, useWorkspaceWebsocket } from "@hooks";
import { HtmlParser } from "@utils";
import { TemplatePreview } from "@tools";
import { CreateTemplateDialog } from "@dialogs";
import { Button, PopMenu, Dialog, Alert, Tag, TooltipIcon, ColorPicker, LabelList } from "@components";
import { Grid, Link, CircularProgress, IconButton } from "@material-ui/core";
import { Pencil, Search, Trash } from "@assets";
import { PaletteOutlined } from "@material-ui/icons";
import { MAX_PAGE_WIDTH } from "@styles/themes";
import useStyle from "./TemplateCard.style";
import classnames from "classnames";

export const TemplateCard = ({
  id,
  uuid,
  name,
  metadata,
  content: description,
  types,
  color,
  thumb,
  company,
  isDefault,
  onSelect,
  onSetDefault,
  maxDescLength=130,
  pickerActionButtons = false,
}) => {
  const classes = useStyle();
  const { t } = useTranslation();
  const socket = useWorkspaceWebsocket();
  const { workTypes } = useActiveProjectStore();
  const { userStore } = useStores();
  const navigate = useNavigate();
  
  const {
    projectTypes,
    removeTemplate,
    updateTemplate,
    addNewProjectType,
    updateProjectTypes,
  } = useTemplates();
  
  const { isManager } = userStore;

  const [ popupAnchor, setPopupAnchor ] = useState(null);
  const [ detailsVisible, showDetails ] = useState(false);
  const [ isLoadingPreview, loadingPreview ] = useState(false);
  const [ previewModalOpened, openPreviewModal ] = useState(false);
  const [ previewData, setPreviewData ] = useState(null);
  const [ commentList, setCommentList ] = useState(null);
  const [ versionData, setVersionData ] = useState(null);
  const [ deleteDialog, setDeleteDialog ] = useState(false);
  const [ colorPickerVisible, showColorPicker ] = useState(false);
  const [ alertLoading, setAlertLoading ] = useState(false);
  const [ showEditionModal, setShowEditionModal ] = useState(false);
  
  const pickerAnchor = useRef(null);
  
  const editable = isManager && company && !pickerActionButtons;
  
  const accentColor = useMemo(() => (
    color || (types?.[0] && projectTypes.find(p => p.id === types[0])?.backgroundColor)
  ), [color, types, projectTypes]);
  
  const usedProjectTypes = useMemo(() => (
    projectTypes?.filter((type) => types?.includes(type.id)) || []
  ), [types, types?.length, projectTypes, projectTypes?.length]);

  const handleClick = () => {
    if (!detailsVisible && !previewModalOpened)
      onSelect(id, uuid, metadata && Object.values(JSON.parse(metadata)?.workTypes).map(wT => wT.id));
  };
  
  const handleEditorOpen = () => navigate(templateEditor(uuid));
  
  const handlePreview = async (e) => {
    e.stopPropagation();
    loadingPreview(true);
    try {
      const {
        structure,
        comments,
        versions,
      } = await getTemplatePreviewQuery(id);

      // let structureData;
      // if(typeof structure === "string") {// may be blank
      //   try {
      //     structureData = JSON.parse(structure);
      //   } catch(e) {
      //     console.log("NO PARSE");
      //     try {
      //       structureData = JSON.parse(unzip(structure));
      //     } catch(e) {
      //       console.log("FIRST PARSE");
      //       try {
      //         structureData = JSON.parse(unzip(unzip(structure)));
      //       } catch(e) {
      //         console.log("DOUBLE PARSE");
      //       }
      //     }
      //   }
      // } else if(structure)
      //   structureData = structure;
      // setPreviewData(structureData);
      setPreviewData(structure);
      if(comments)
        setCommentList(comments);
      if(versions)
        setVersionData(versions);
    } catch (e) {
      setPreviewData(null);
      if(e.code) {
        console.error(e.code);
        // @todo: show it as alert?
      }
    }
    loadingPreview(false);
    openPreviewModal(true);
  };
  const handleDetails = (e) => {
    e.stopPropagation();
    showDetails(!detailsVisible);
  };

  const closeModal = () => {
    openPreviewModal(false);
    setPreviewData(null);
  };
  
  const handleSetAsDefault = () => {
    onSetDefault?.(isDefault ? undefined : id);
  }

  const handleRemove = async () => {
    setAlertLoading(true);
    if(isDefault)
      onSetDefault?.(undefined);
    await socket.requestTemplateRemove(id);
    removeTemplate(id);
    setAlertLoading(false);
    setDeleteDialog(false);
  };

  const handleColorConfirm = async (color) => {
    setAlertLoading(true);
    await saveTemplateQuery(id, { color });
    updateTemplate({ id, color })
    showColorPicker(false);
    setAlertLoading(false);
  };

  const handleColorChange = async (color) => {
    updateTemplate({ id, color })
  };
  
  return (
    <Grid item xs={isMobile ? 12 : 6} md={isMobile ? 12 : 4}>
      <Grid
        item
        container
        direction="column"
        justifyContent="center"
        wrap="nowrap"
        className="hoverRow rounded-2 border-solid border-light box-border overflow-hidden bg-secondary relative"
        role="listitem"
      >
        <Grid item container className={classes.gfx}>
          {
            thumb
              ? <img src={thumb} alt={"template graphic"} />
              : <Grid item container className={classes.accent} style={{ backgroundColor: accentColor }} />
          }
        </Grid>
        <Grid
          item
          container
          wrap="nowrap"
          direction="column"
          justifyContent="space-between"
          className={classes.info}
          style={{ height: 190 + 3 * Math.floor(Math.sqrt(maxDescLength)) }}
        >
          <Grid item container alignItems="center">
            <h3 className={classes.title}>{name}</h3>
            {
              editable &&
              <TooltipIcon
                size="small"
                color="primary"
                onClick={() => setShowEditionModal(true)}
                className="hoverIcon ml-1"
                aria-label="edit template info"
                icon={<Pencil />}
              >
                {t("views.templates.edit_info")}
              </TooltipIcon>
            }
            {
              !pickerActionButtons &&
              <TooltipIcon
                size="small"
                color="primary"
                onClick={handlePreview}
                className="hoverIcon ml-1"
                aria-label="preview template"
                icon={isLoadingPreview ? <CircularProgress color="inherit" size="1.125rem" /> : <Search />}
              >
                {t("views.templates.card.preview")}
              </TooltipIcon>
            }
          </Grid>
          {
            !!description &&
            <Grid item container alignItems="center">
              <p style={{ margin: 0, fontSize: 14 }}>
                {(() => {
                  const p = description.replace(/<[^>]*>/g, "");
                  if (p.length > maxDescLength)
                    return p.slice(0, maxDescLength).replace(/ \w+,?$/, "");
                  return p;
                })()}
                {
                  description.length > maxDescLength &&
                  <Link ref={setPopupAnchor} onClick={handleDetails}>
                    {" ("}
                    {t("views.templates.card.read_more")}
                    {"...)"}
                  </Link>
                }
              </p>
              {
                editable &&
                <TooltipIcon
                  size="small"
                  color="primary"
                  onClick={() => setShowEditionModal(true)}
                  aria-label="edit template info"
                  className="hoverIcon ml-1"
                  icon={<Pencil />}
                >
                  {t("views.templates.edit_info")}
                </TooltipIcon>
              }
            </Grid>
          }
          {
            types.length > 0 &&
            <Grid item container alignItems="center">
              <LabelList labels={usedProjectTypes} />
              {
                editable &&
                <TooltipIcon
                  size="small"
                  color="primary"
                  onClick={() => setShowEditionModal(true)}
                  aria-label="edit template info"
                  className="hoverIcon ml-1"
                  icon={<Pencil />}
                >
                  {t("views.templates.edit_info")}
                </TooltipIcon>
              }
            </Grid>
          }
          <Grid
            item
            container
            style={{ width: "calc(100% + 24px)", margin: "0 -12px" }}
          >
            {
              pickerActionButtons
                ? <>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    className={classes.button}
                    aria-label="preview template"
                    onClick={handlePreview}
                  >
                    {t("views.templates.card.preview")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    className={classnames(classes.button, "border-primary")}
                    aria-label="edit template"
                    onClick={handleClick}
                  >
                    {t(`views.templates.card.select`)}
                  </Button>
                </>
                : <>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    className={classes.button}
                    aria-label="select template"
                    onClick={handleClick}
                  >
                    {t("views.templates.card.create_project")}
                  </Button>
                  {
                    company
                      ? <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={classnames(classes.button, "border-primary")}
                        aria-label="edit template"
                        onClick={handleEditorOpen}
                      >
                        {t(`views.templates.card.${ isManager ? "open_edit" : "open" }`)}
                      </Button>
                      : <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={classes.button}
                        aria-label="preview template"
                        onClick={handlePreview}
                      >
                        {t("views.templates.card.preview")}
                      </Button>
                  }
                </>
            }
          </Grid>
        </Grid>
        {
          isDefault &&
          <Grid item container className={classes.actionButtonContainer}>
            <Tag forceDot text={t("views.templates.default_template")} />
          </Grid>
        }
        {
          isManager && !pickerActionButtons &&
          <Grid
            item container
            className={classnames(!colorPickerVisible && "hoverIcon", classes.actionButtonContainer)}
          >
            <Button
              onClick={handleSetAsDefault}
              size="small"
              className={classes.setDefaultButton}
            >
              {t(isDefault ? "common.default_unset" : "common.default_set")}
            </Button>
            <TooltipIcon
              ref={pickerAnchor}
              size="small"
              color="primary"
              onClick={() => showColorPicker(true)}
              className="ml-1"
              icon={<PaletteOutlined />}
            >
              {t("views.templates.change_color")}
            </TooltipIcon>
            {
              company &&
              <IconButton size="small" onClick={() => setDeleteDialog(true)}>
                <Trash color="error" />
              </IconButton>
            }
          </Grid>
        }
      </Grid>
      {
        deleteDialog &&
        <Alert
          isOpen={deleteDialog}
          onAccept={handleRemove}
          onCancel={() => setDeleteDialog(false)}
          title={t("common.delete_template_question", {template: name})}
          isAcceptLoading={alertLoading}
          acceptText={t("common.delete_accept")}
        />
      }
      {
        description &&
        <PopMenu
          show={detailsVisible}
          anchor={popupAnchor}
          onClickAway={() => showDetails(false)}
          style={{ width: 560, padding: 24 }}
        >
          <p>{HtmlParser(description)}</p>
        </PopMenu>
      }
      {
        showEditionModal &&
        <CreateTemplateDialog
          open={showEditionModal}
          onChange={updateTemplate}
          onClose={() => setShowEditionModal(false)}
          defaultValues={{ id, name, description, types }}
          projectTypes={projectTypes}
          onNewTypeCreate={addNewProjectType}
          onProjectTypesChange={updateProjectTypes}
        />
      }
      {
        previewData && previewModalOpened &&
        <Dialog
          className={classes.dialogPaper}
          containerClass="px-12 bg-primary"
          open={previewModalOpened}
          title={t("views.templates.card.preview_title", { name })}
          onClose={closeModal}
          width={MAX_PAGE_WIDTH + 48}
        >
          <TemplatePreview
            versionStructure={previewData}
            workTypes={workTypes}
            commentList={commentList}
            versionData={versionData}
          />
        </Dialog>
      }
      {
        colorPickerVisible &&
        <ColorPicker
          anchor={pickerAnchor.current}
          show={colorPickerVisible}
          revertOnCancel
          initialColor={accentColor}
          onClose={() => showColorPicker(false)}
          onChange={handleColorChange}
          onAccept={handleColorConfirm}
          acceptButtonTitle={t("views.templates.set_color")}
          placement="bottom"
        />
      }
    </Grid>
  );
};

TemplateCard.propTypes = {
  id: oneOfType([string, number]).isRequired,
  uuid: string.isRequired,
  types: arrayOf(number),
  name: string.isRequired,
  content: string,
  company: bool,
  color: string,
  onSelect: func.isRequired,
  showGraphic: bool,
  icon: node,
  maxDescLength: number,
  isDefault: bool,
  onSetDefault: func,
  metadata: string, //JSON string
  thumb: string,
  pickerActionButtons: bool,
};